/** Australian state/territory codes — keep uppercase in display. */
const AU_STATES = new Set(['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'])

/**
 * Title-case a single token for address display.
 * Matches how addresses are shown in the iOS app (e.g. "Nicholson Street", "Fitzroy VIC 3065").
 */
function titleCaseAddressToken(token: string): string {
  if (!token) return token
  if (/^\d+[A-Za-z]?$/.test(token)) return token.toUpperCase() === token ? token : token
  // Unit/street numbers like "12/10" or "10A"
  if (/^\d/.test(token) && /[/\d]/.test(token)) {
    return token
      .split('/')
      .map((part) => {
        if (/^\d+[A-Za-z]?$/.test(part)) {
          // "10a" → "10A"
          const m = part.match(/^(\d+)([A-Za-z]?)$/)
          if (!m) return part
          return m[1] + (m[2] ? m[2].toUpperCase() : '')
        }
        return titleCaseWord(part)
      })
      .join('/')
  }
  const upper = token.toUpperCase()
  if (AU_STATES.has(upper)) return upper
  if (/^\d+$/.test(token)) return token
  return token
    .split('-')
    .map(titleCaseWord)
    .join('-')
}

function titleCaseWord(word: string): string {
  if (!word) return word
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}

/** Display form of a full property address (title case, AU state codes preserved). */
export function formatAddressDisplay(fullAddress: string): string {
  const trimmed = fullAddress?.trim()
  if (!trimmed) return ''
  return trimmed
    .split(/,\s*/)
    .map((segment) =>
      segment
        .split(/\s+/)
        .filter(Boolean)
        .map(titleCaseAddressToken)
        .join(' '),
    )
    .join(', ')
}

/** Same split as iOS `addressFirstPart` / `addressSecondPart` on MapPropertyCard. */
export function splitAddressDisplay(fullAddress: string): { street: string; locality: string } {
  const formatted = formatAddressDisplay(fullAddress)
  const components = formatted.split(', ')
  const street = components[0] || formatted
  const locality = components.length > 1 ? components.slice(1).join(', ') : ''
  return { street, locality }
}

/** Natural address order — unit numbers sort numerically (2/130 before 11/130). */
export function compareAddressesNatural(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}
